import { getToolName, isToolUIPart, type UIMessage } from "ai";

export type WebResearchStatus = "idle" | "searching" | "reviewing" | "preparing";

export type WebResearchSource = {
  title: string;
  url: string;
  domain: string;
};

const SEARCH_TOOL = "tavily-search";
const EXTRACT_TOOL = "tavily-extract";

function isResearchToolName(name: string): boolean {
  return name === SEARCH_TOOL || name === EXTRACT_TOOL;
}

function isToolComplete(state: string | undefined): boolean {
  return state === "output-available" || state === "output-error";
}

/**
 * Missing/unknown state counts as in-progress only while streaming.
 * Persisted tool parts without a live stream are treated as idle for status.
 */
function isToolInProgress(state: string | undefined, streaming: boolean): boolean {
  if (!streaming) return false;
  if (!state) return true;
  return !isToolComplete(state);
}

/** Hostname without leading `www.`, or empty when the URL is invalid. */
export function domainFromUrl(url: string): string {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function queryFromToolInput(input: unknown): string | null {
  if (!input || typeof input !== "object") return null;
  const query = (input as { query?: unknown }).query;
  if (typeof query !== "string") return null;
  const trimmed = query.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function sourceFromUnknown(entry: unknown): WebResearchSource | null {
  if (!entry || typeof entry !== "object") return null;
  const record = entry as { title?: unknown; url?: unknown };
  const url = typeof record.url === "string" ? record.url.trim() : "";
  if (!url) return null;
  const title =
    typeof record.title === "string" && record.title.trim().length > 0
      ? record.title.trim()
      : url;
  return { title, url, domain: domainFromUrl(url) };
}

function sourcesFromToolOutput(output: unknown): WebResearchSource[] {
  if (!output || typeof output !== "object") return [];
  const results = (output as { results?: unknown }).results;
  if (!Array.isArray(results)) return [];
  return results.flatMap((entry) => {
    const source = sourceFromUnknown(entry);
    return source ? [source] : [];
  });
}

function collectSources(
  output: unknown,
  seen: Set<string>,
  sources: WebResearchSource[]
): void {
  for (const source of sourcesFromToolOutput(output)) {
    if (seen.has(source.url)) continue;
    seen.add(source.url);
    sources.push(source);
  }
}

/**
 * Parses Tavily tool parts on an assistant message into research status + source chips.
 */
export function inspectWebResearchMessage(
  message: UIMessage | null | undefined,
  options?: { hasText?: boolean; streaming?: boolean; isResearchRoute?: boolean }
): {
  status: WebResearchStatus;
  statusLabel: string | null;
  query: string | null;
  sources: WebResearchSource[];
  hasResearchTools: boolean;
} {
  const hasText = options?.hasText ?? false;
  const streaming = options?.streaming ?? false;
  const isResearchRoute = options?.isResearchRoute ?? false;

  const parts = Array.isArray(message?.parts) ? message!.parts : [];
  let hasSearch = false;
  let hasExtract = false;
  let searchInProgress = false;
  let extractInProgress = false;
  let query: string | null = null;
  const sources: WebResearchSource[] = [];
  const seen = new Set<string>();

  for (const part of parts) {
    if (!isToolUIPart(part)) continue;
    const name = getToolName(part);
    if (!isResearchToolName(name)) continue;

    if (name === SEARCH_TOOL) {
      hasSearch = true;
      if (isToolInProgress(part.state, streaming)) searchInProgress = true;
      const partQuery = queryFromToolInput(part.input);
      if (partQuery) query = partQuery;
      // Prefer output-available; also accept output when present (odd persisted shapes).
      if (part.state === "output-available" || part.output != null) {
        collectSources(part.output, seen, sources);
      }
    } else if (name === EXTRACT_TOOL) {
      hasExtract = true;
      if (isToolInProgress(part.state, streaming)) extractInProgress = true;
      if (part.state === "output-available" || part.output != null) {
        collectSources(part.output, seen, sources);
      }
    }
  }

  const hasResearchTools = hasSearch || hasExtract;

  let status: WebResearchStatus = "idle";
  if (extractInProgress) {
    status = "reviewing";
  } else if (searchInProgress) {
    status = "searching";
  } else if (hasResearchTools && !hasText && streaming) {
    status = "preparing";
  } else if (isResearchRoute && !hasResearchTools && !hasText && streaming) {
    status = "searching";
  }

  const statusLabel =
    status === "searching"
      ? "Searching the web…"
      : status === "reviewing"
        ? "Reviewing relevant sources…"
        : status === "preparing"
          ? "Preparing your answer…"
          : null;

  return { status, statusLabel, query, sources, hasResearchTools };
}

/** Minimum joined text length treated as a substantial researched answer. */
export const SUBSTANTIAL_RESEARCH_ANSWER_CHARS = 400;

/**
 * True when Tavily tools ran but the idle message only has a short preamble
 * (common when the agent hits the step budget before synthesizing).
 */
export function isIncompleteResearchAnswer(
  message: UIMessage | null | undefined,
  options?: { text?: string; streaming?: boolean; busy?: boolean }
): boolean {
  if (options?.streaming || options?.busy) return false;
  const { hasResearchTools } = inspectWebResearchMessage(message, {
    hasText: Boolean(options?.text?.trim()),
    streaming: false,
  });
  if (!hasResearchTools) return false;
  const text = (options?.text ?? "").trim();
  return text.length < SUBSTANTIAL_RESEARCH_ANSWER_CHARS;
}
